import { action, computed, observable, toJS, when } from "mobx";
import _, { defaultTo, isEmpty, toString } from "lodash";
import { FormStore, isField } from "@modules/formx";
import { store } from "@store";
import { parseFloatOrDefault } from "@bonliva-crm/common";
import {
  BookingFinanceEditor_Fetch,
  BookingFinanceEditor_ItemFragment,
  BookingFinanceEditor_Update,
  UploadBlob,
  LegalPersonType,
  UpdateBookingFinanceInput,
  BookingFinanceSearch_Get,
  BookingFinanceEditor_UnlockForms as unlockForms,
  BookingFinanceEditor_FetchDraftCosts as fetchDraftCosts,
  BookingEventLog_GetEvents,
  BookingInvoicing_Fetch,
  TimeReportStatus,
  TimereportKitSupportSearch_Get,
  ContractType,
  NodeType
} from "@graphql";
import locale from "@bonliva-crm/locale";
import { BookingFinance } from "./BookingFinance";
import { SubcontractorDocument } from "./SubcontractorDocs/SubcontractorDocument";
import { payoutFormScheme } from "@bookings/BookingFinanceEditor/utils";

export class BookingFinanceFormStore extends FormStore<BookingFinance> {
  @observable bookingData: BookingFinanceEditor_ItemFragment;
  @observable subcontractorDocsToUpload: File[] = [];
  @observable subcontractorDocuments: Array<SubcontractorDocument & { blobId?: string }> =
    [];
  @observable missedFields: string[] = [];
  @observable canAddPayForm = true;

  @observable unlockForms = false;

  // CRMDEV-7112: counter, not boolean — the sick-pay fetch turns on
  // IsConsultantPayed mid-flight, which starts a second overlapping fetch;
  // a boolean would flip to "not loading" when the first one finishes.
  @observable private draftCostsInFlight = 0;

  @computed get isDraftCostsLoading() {
    return this.draftCostsInFlight > 0;
  }

  constructor() {
    super({
      debounceValidationMs: 250,
      classValidatorClassType: BookingFinance,
      exposeAs: "bookingFinanceEditorStore",
      initialValues: new BookingFinance(),
      onSubmit: () => this.onSubmit()
    });

    this.onSetValue((options) => {
      if (isField(options, "LegalPersonType")) {
        this.values.ContractType = null;
      }

      if (isField(options, "IsConsultantPayed") && options.value === true) {
        this.fetchDraftCosts(true, false);
      }

      if (isField(options, "IsSickPayed") && options.value === true) {
        this.fetchDraftCosts(false, true);
      }
    });

    (window as any).bookingFinanceEditorStore = this;
  }

  get customDirty() {
    return this.dirty || this.subcontractorDocumentsChanged;
  }

  get canReadSubcontractorDocuments() {
    return store().user.can("booking-subcontractor-invoice:read:all");
  }

  get canUploadSubcontractorDocuments() {
    return store().user.can("booking-subcontractor-invoice:create:all");
  }

  get canDeleteSubcontractorDocuments() {
    return store().user.can("booking-subcontractor-invoice:delete:all");
  }

  @computed get LegalPersonType() {
    return this.values.LegalPersonType;
  }

  @computed get contactType() {
    return this.bookingData?.Consultant?.contactType;
  }

  @computed get isNationalProcurement() {
    return this.bookingData?.BookingGroup?.isNationalProcurement;
  }

  @computed get contractTypes(): ContractType[] {
    if (this.isNationalProcurement) {
      return Object.values(ContractType);
    }

    return Object.values(ContractType).filter(
      (type) => type !== ContractType.NationalProcurement
    );
  }

  @action checkCanAddPayForm = async (
    data: BookingFinanceEditor_ItemFragment["Consultant"]["contactType"]
  ) => {
    const missing = [];
    const opt = { abortEarly: false };
    await payoutFormScheme.validate(data, opt).catch((rej) => {
      missing.push(`${locale.Economy}: ${rej.errors.join(", ")}.`);
    });
    this.missedFields = missing;
    this.canAddPayForm = isEmpty(missing);
  };

  init = async (bookingData: BookingFinanceEditor_ItemFragment) => {
    this.bookingData = bookingData;
    await this.checkCanAddPayForm(this.contactType);
    this.reset(BookingFinance.FromGql(bookingData));
    this.subcontractorDocuments = bookingData.SubcontractorDocuments;
    this.subcontractorDocsToUpload = [];
    this.unlockForms = bookingData.unlockForms;
  };

  onSubmit = async () => {
    try {
      // CRMDEV-7112: the draft-cost fetch triggered by toggling IsConsultantPayed is
      // fire-and-forget; submitting before it resolves would send SubcontractorInvoiceTotal: 0
      await when(() => !this.isDraftCostsLoading);

      const data = this.getUpdateInput();
      await BookingFinanceEditor_Update.func(
        { bookingId: this.bookingData._id, data },
        {
          refetchQueries: [
            BookingFinanceEditor_Fetch.OperationName,
            BookingFinanceSearch_Get.OperationName,
            TimereportKitSupportSearch_Get.OperationName
          ]
        }
      ).then((res) => {
        // sorry for this hack
        // Remove booking week from search result
        const kitFlowSearch = (window as any)?.bookingKitSupportSearchStore;
        const kitFlowFilter = (window as any)?.bookingKitSupportSearchStore
          ?.filterManager;

        if (kitFlowSearch && kitFlowFilter) {
          const timeReportStatus = kitFlowFilter.filter?.timeReportStatus;
          if (
            timeReportStatus.length > 0 &&
            !timeReportStatus.includes(TimeReportStatus.ConsultantPaymentProcessed) &&
            data.IsConsultantPayed
          ) {
            kitFlowSearch.deleteRowByBookingId(this.bookingData._id);
          }
        }

        this.init(res.data.updateBookingFinance);

        store().localPubSub.financeFormUpdated.trigger();
      });
      store().snackbars.success(locale.BookingUpdated);
    } catch (e) {
      store().snackbars.error(`Cannot save booking\nReason: ${e}`);
    }
  };

  removeSubcontractorDocument = (id: string) => {
    this.subcontractorDocuments = this.subcontractorDocuments
      .map((x) => {
        if (x.FileRef.NodeID === id) {
          x.isDeleted = true;
        }
        return x;
      })
      .filter((x) => !(x.isDeleted && x.isNew));
  };

  openSubcontractorDocumentUpload = (files?: File[]) => {
    if (files) {
      this.subcontractorDocsToUpload = files;
      return;
    }

    store()
      .upload.requestFileUpload({ multiple: true })
      .then((files) => {
        this.subcontractorDocsToUpload = files;
      });
  };

  uploadSubcontractorDocuments = async () => {
    if (_.isEmpty(this.subcontractorDocsToUpload)) {
      return;
    }

    for (const file of this.subcontractorDocsToUpload) {
      const { data } = await UploadBlob.func({ file });

      this.subcontractorDocuments.push({
        DocumentType: "",
        blobId: data.uploadBlob,
        FileRef: {
          Name: file.name,
          MimeType: file.type,
          NodeID: "",
          Size: file.size,
          NodeType: NodeType.File
        },
        isNew: true,
        isDeleted: false,
        ThumbURL: "",
        PdfURL: ""
      });
    }

    this.subcontractorDocsToUpload = [];
  };

  @computed get subcontractorDocumentsChanged() {
    return this.subcontractorDocuments.any((x) => x.isDeleted || x.isNew);
  }

  private getUpdateInput(): UpdateBookingFinanceInput {
    const newSCDocs = this.subcontractorDocuments
      .filter((x) => x.isNew)
      .map((x) => x.blobId);

    const deletedSCDocs = this.subcontractorDocuments
      .filter((x) => x.isDeleted)
      .map((x) => x.FileRef.NodeID);

    const values = toJS(this.values);
    // resetting value if type is changed to Sub Contractor
    values.ContractType =
      this.LegalPersonType === LegalPersonType.Employee ? values.ContractType : null;
    // values.NewSubcontractorDocuments = newSCDocs;
    values.DeletedSubcontractorDocumentIds = deletedSCDocs;

    values.AccommodationCost = parseFloatOrDefault(values.AccommodationCostStr, 0);
    values.TravelCost = parseFloatOrDefault(values.TravelCostStr, 0);
    values.EmployeeSalary = parseFloatOrDefault(values.EmployeeSalaryStr, 0);
    values.SubcontractorInvoiceTotal = parseFloatOrDefault(
      values.SubcontractorInvoiceTotalStr,
      0
    );
    values.SickPayTotal = parseFloatOrDefault(values.SickPayTotalStr, 0);
    values.GroupInvoiceTotal = parseFloatOrDefault(values.GroupInvoiceTotalStr, 0);
    values.ChangedKeys = changedKeys(toJS(this.initialValues), values);

    delete values.AccommodationCostStr;
    delete values.GroupInvoiceTotalStr;
    delete values.TravelCostStr;
    delete values.EmployeeSalaryStr;
    delete values.SubcontractorInvoiceTotalStr;
    delete values.SickPayTotalStr;

    return {
      AdditionalWork: values.AdditionalWork,
      AdditionalWorkNote: values.AdditionalWorkNote,
      ContractType: values.ContractType,
      IsConsultantPayed: values.IsConsultantPayed,
      Invoices: values.Invoices,
      GroupInvoiceTotal: values.GroupInvoiceTotal,
      BonlivaCareInvoice: values.BonlivaCareInvoice,
      Note: values.Note,
      PatientId: values.PatientId,
      LegalPersonType: values.LegalPersonType,
      BusinessName: values.BusinessName,
      BusinessSocialSecurityNo: values.BusinessSocialSecurityNo,
      UpdateConsultantWithSnapshotData: values.UpdateConsultantWithSnapshotData,
      NewSubcontractorDocuments: newSCDocs,
      DeletedSubcontractorDocumentIds: deletedSCDocs,
      EmployeeSalary: values.EmployeeSalary,
      SubcontractorInvoiceTotal: values.SubcontractorInvoiceTotal,
      AccommodationCost: values.AccommodationCost,
      TravelCost: values.TravelCost,
      CustomerFine: values.CustomerFine,
      ChangedKeys: values.ChangedKeys,
      IsSickPayed: values.IsSickPayed,
      SickPayTotal: values.SickPayTotal
    };
  }

  get isOwnProject() {
    return !!this.bookingData?.Project?.Members.find(
      (x) => x.PersonId === store().user.id
    );
  }

  get isProjectManager() {
    const subords = store().user.getSubordinates();

    // any member is subordinate
    return !!this.bookingData?.Project?.Members.find((x) =>
      subords.any((y) => y._id === x.PersonId)
    );
  }

  public async setUnlockForms(value: boolean) {
    try {
      const { data } = await unlockForms.func(
        { bookingId: this.bookingData._id, value },
        {
          fetchPolicy: "network-only",
          refetchQueries: [
            BookingEventLog_GetEvents.OperationName,
            BookingInvoicing_Fetch.OperationName
          ]
        }
      );
      this.unlockForms = data.setUnlockBookingForms.unlockForms;
      store().snackbars.success(locale.BookingUpdated);
      const bookingInvoiceStore = (window as any).bookingInvoiceStore;
      bookingInvoiceStore?.fetch();
    } catch (e) {
      store().snackbars.error(`Cannot save booking\nReason: ${e}`);
    }
  }

  public async fetchDraftCosts(regular: boolean, sick: boolean) {
    try {
      this.draftCostsInFlight++;
      const { data } = await fetchDraftCosts.func({ id: this.bookingData._id });
      const draftCosts = data.bookingById?.draftCosts;

      if (regular && this.values.IsConsultantPayed) {
        this.setValue("EmployeeSalary", draftCosts.employeeSalary);
        this.setValue(
          "EmployeeSalaryStr",
          toString(defaultTo(draftCosts.employeeSalary, 0))
        );

        this.setValue("TravelCost", draftCosts.travel);
        this.setValue("TravelCostStr", toString(defaultTo(draftCosts.travel, 0)));

        this.setValue("AccommodationCost", draftCosts.accommodation);
        this.setValue(
          "AccommodationCostStr",
          toString(defaultTo(draftCosts.accommodation, 0))
        );

        this.setValue("SubcontractorInvoiceTotal", draftCosts.invoiceTotal);
        this.setValue(
          "SubcontractorInvoiceTotalStr",
          toString(defaultTo(draftCosts.invoiceTotal, 0))
        );
      }

      if (sick && this.values.IsSickPayed) {
        this.setValue("IsConsultantPayed", this.values.IsSickPayed);
        this.setValue("SickPayTotal", draftCosts.sickPayTotal);
        this.setValue("SickPayTotalStr", toString(defaultTo(draftCosts.sickPayTotal, 0)));
      }
    } catch (e) {
      store().snackbars.error(`Cannot fetch draft costs\nReason: ${e}`);
    } finally {
      this.draftCostsInFlight--;
    }
  }
}

const changedKeys = (o1: any, o2: any) => {
  const keys = _.union(_.keys(o1), _.keys(o2));
  return _.filter(keys, (x) => !_.isEqual(o1[x], o2[x]));
};
